// src/components/ConsentModal.js
import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, FormControlLabel, Checkbox, Typography, Box, Link, CircularProgress
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { API_URL } from '../config';

const ConsentModal = ({ open, onAccepted, onCancel }) => {
  const [consentOCR, setConsentOCR] = useState(false);
  const [consentAI, setConsentAI]   = useState(false);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');

  const canProceed = consentOCR && consentAI;

  const handleAccept = async () => {
    if (!canProceed) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/accept-terms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ consentOCR: true, consentAI: true }),
      });
      if (!res.ok) throw new Error('Errore registrazione consenso');
      const data = await res.json();

      localStorage.setItem('trmUserHash', data.userHash);
      localStorage.setItem('trmConsentTimestamp', new Date().toISOString());
      onAccepted();
    } catch (err) {
      console.error(err);
      setError('Errore nel salvataggio del consenso. Controlla la connessione e riprova.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setConsentOCR(false);
    setConsentAI(false);
    setError('');
    onCancel();
  };

  return (
    <Dialog open={open} fullWidth maxWidth="sm" disableEscapeKeyDown>
      <DialogTitle sx={{ fontWeight: 700 }}>
        Consenso al trattamento dei dati sanitari
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Prima di elaborare il tuo referto, trattiamo <strong>dati sanitari speciali</strong> ai sensi dell'Art.9 GDPR.
          Leggi l'{' '}
          <Link component={RouterLink} to="/privacy-policy" target="_blank" rel="noopener">
            informativa privacy completa
          </Link>
          {' '}e fornisci il consenso esplicito per procedere.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={consentOCR}
                onChange={e => setConsentOCR(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography variant="body2">
                <strong>Accetto l'estrazione OCR del testo</strong> dal documento caricato
              </Typography>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={consentAI}
                onChange={e => setConsentAI(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography variant="body2">
                <strong>Accetto l'elaborazione AI</strong> per la semplificazione del referto (EU AI Act)
              </Typography>
            }
          />
        </Box>

        {error && (
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          Puoi revocare il consenso in qualsiasi momento da "Gestisci Privacy" in fondo alla pagina (Art.7(3) GDPR).
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleCancel} variant="outlined" color="secondary" disabled={loading}>
          Annulla
        </Button>
        <Button
          onClick={handleAccept}
          variant="contained"
          color="primary"
          disabled={!canProceed || loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {loading ? 'Registrazione...' : 'Accetta e continua'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConsentModal;
